import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { Dropdown } from 'react-bootstrap';

import AddCommentBox from './AddCommentBox';
import CommentList from './CommentList';

import Avatar from '../../../shared/_TBComponents/Avatar/Avatar';
import Text from '../../../shared/_TBComponents/Text';
import iconSet from '../../../shared/images/teambuildr-selection.json';

import { getImageSource } from '../utils';
import {
  ColorGray,
  ColorGreen,
  ColorPrimary,
  ColorWhite,
} from '../../../shared/GlobalStyles';

import { showErrorToast } from '../../../shared/utils/toastManager';
import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import {
  deleteMeJournalEntries,
  openImageLightbox,
  openMessageVideo,
  setActiveReduxModal,
  setEditJournalEntry,
  setReportPostId,
} from '../ducks/journalActions';

const DROPDOWN_TEXT_PROPS = {
  fontSize: 14,
  marginLeft: '10px',
  paddingTop: '2px',
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: 1.3,
  color: ColorGray,
};

const MEDIA_BOX_STYLES = `
  align-items: center;
  display: flex;
  height: 100px;
  justify-content: center;
  min-height: 100px;
  min-width: 100px;
  position: relative;
  width: 100px;
`;

const AuthorAvatarContainer = styled('div')`
  align-items: center;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const AuthorInfoContainer = styled('div')`
  align-items: center;
  display: flex;
  padding-right: 5px;
`;

const AuthorName = styled('div')`
  color: ${ColorGray};
  font-size: 13px;
  line-height: 19.5px;
  margin-right: 12px;
  @media screen and (max-width: 480px) {
    margin-right: 9px;
  }
`;

const CommentCount = styled('div')`
  align-items: center;
  display: flex;
`;

const CommentCountText = styled('div')`
  color: ${(props) => (props.commentsCollapsed ? ColorGray : 'orange')};
  font-size: 13px;
  line-height: 19.5px;
`;

const CommentIconContainer = styled('div')`
  align-items: center;
  display: flex;
  height: 28px;
  justify-content: center;
  margin-right: 4px;
  width: 28px;
`;

const CommentsContainer = styled('button')`
  background-color: transparent;
  border: none;
  cursor: ${(props) => (props.count > 0 ? 'pointer' : 'default')};
`;

const DateAndPrivacyContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
`;

const EntryDate = styled('div')`
  color: ${ColorGray};
  font-size: 16px;
  line-height: 24px;
`;

const EntryDetailsContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const EntryHeaderContainer = styled('div')`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const EntryText = styled('div')`
  font-size: 19px;
  line-height: 28.5px;
  margin-bottom: 16px;
  overflow-x: hidden;
`;

const EntryTitle = styled('div')`
  color: ${(props) => (props.title ? ColorPrimary : ColorGray)};
  font-size: 19px;
  font-weight: 700;
  line-height: 28.5px;
`;

const GradientBar = styled('div')`
  background: ${(props) => `linear-gradient(134deg, ${props.gradientBarColors[0]} -8%, ${props.gradientBarColors[1]} 200%)`};
  border-bottom: 8px solid ${ColorWhite};
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  height: 12px;
  margin: -20px 0 10px -20px;
  width: calc(100% + 40px);
`;

const JournalEntryContainer = styled('li')`
  background: ${ColorWhite};
  border-radius: 12px;
  box-shadow: 0px 2px 4px 0px rgb(0 0 0 / 10%);
  font-family: 'Nunito Sans';
  list-style-type: none;
  padding: 20px;
  position: relative;
`;

const MediaBox = styled('button')`
  ${MEDIA_BOX_STYLES}
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const MediaBoxDiv = styled('div')`
  ${MEDIA_BOX_STYLES}
`;

const MediaContainer = styled('div')``;

const MediaHeader = styled('h3')`
  color: ${ColorPrimary};
  font-size: 13px;
  font-weight: 700;
  line-height: 19.5px;
  margin-bottom: 8px;
`;

const MediaImage = styled('img')`
  border-radius: 10px;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const MediaItem = styled('div')`
  align-content: center;
  display: flex;
  flex-direction: column;
  margin-right: 16px;
`;

const MediaItems = styled('div')`
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  margin-bottom: 12px;
  overflow-x: auto;
  width: 100%;
`;

const MediaTitle = styled('div')`
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 4px;
  margin-top: 4px;
  text-align: center;
`;

const OptionsDropdownWrapper = styled('div')`
  .btn-primary {
    background-color: transparent;
    border: none;
    height: 20px;
    padding: 0;
    &hover {
      background-color: transparent;
      border: none;
    }
  }
  .cancel-edit {
    align-items: top;
    cursor: pointer;
    display: flex;
    height: 32px;
    text-align: center;
  }
  .show > .btn-primary.dropdown-toggle {
    background-color: transparent;
    border: none;
    color: ${ColorPrimary};
  }
  .dropdown-menu {
    padding: 0px;
  }
  .dropdown-item {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding: 5px;
    padding-left: 10px;
    transition: none;
    text-transform: uppercase;
    svg {
      fill: ${ColorGray};
    }
    :hover {
      transition: linear 0s;
      background: ${ColorGreen};
      color: ${ColorWhite};
      div p {
        color: ${ColorWhite};
      }
      svg {
        fill: ${ColorWhite};
      }
    }
  }
`;

const OptionsIconContainer = styled('div')`
  align-items: top;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const PlayIconContainer = styled('button')`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 60px;
  justify-content: center;
  left: 0;
  margin-left: auto;
  margin-right: auto;
  position: absolute;
  right: 0;
  width: 60px;
  z-index: 1;
`;

const PrivacyIconContainer = styled('div')`
  align-items: center;
  display: flex;
  height: 20px;
  justify-content: center;
  width: 20px;
`;

const JournalEntry = ({ journalEntry }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const { activeTab, currentAthlete, editedComment } = useSelector(
    (state) => state.journal.data,
  );

  const [commentsCollapsed, setCommentsCollapsed] = useState(true);
  const [newCommentText, setNewCommentText] = useState('');

  const dispatch = useDispatch();

  const {
    body,
    createdAt,
    createdByUserId,
    createdByUserGradientColors,
    createdByUserName,
    createdByUserPic,
    media,
    private: isPrivate,
  } = journalEntry;

  const commentsCount = journalEntry?.comments?.length;
  const { id: currentUserId, accountCode } = currentUser;
  const currentUserIsAuthor = currentUserId === createdByUserId;
  const hasCommentEditing = editedComment.entryId === journalEntry.id;
  const showGradientBar = activeTab === 'SINGLE_ATHLETE' && currentAthlete.id !== createdByUserId;
  const title = journalEntry.associatedExerciseName || journalEntry.title;

  const handleEditClick = () => {
    if (journalEntry.associatedExerciseName) {
      showErrorToast('Entries associated with a workout can only be edited within workout entry.');
      return;
    }
    dispatch(setActiveReduxModal('edit-entry-modal'));
    dispatch(setEditJournalEntry(journalEntry));
    journalTracker('Journal entry', 'Edit entry modal opened');
  };

  const handleDeleteClick = () => {
    if (journalEntry.associatedExerciseName) {
      showErrorToast('Entries associated with a workout cannot be deleted.');
      return;
    }
    const values = { journalEntryIds: [journalEntry.id] };
    dispatch(deleteMeJournalEntries(accountCode, values));
    journalTracker('Journal entry', 'Entry deleted');
  };

  const handleImageClick = (mediaUrl) => {
    dispatch(openImageLightbox(mediaUrl));
    journalTracker(
      'Journal entry',
      'Image clicked - Image lightbox modal opened',
    );
  };

  const handleVideoClick = (e, mediaUrl) => {
    e.preventDefault();
    dispatch(openMessageVideo(mediaUrl));
    journalTracker('Journal entry', 'Video clicked - Play video modal opened');
  };

  const handleCommentsToggle = () => {
    if (commentsCount > 0) {
      setCommentsCollapsed(!commentsCollapsed);
    }
  };

  const handleReportJournalEntryClick = () => {
    dispatch(setActiveReduxModal('report-post-modal'));
    dispatch(setReportPostId('Entry', journalEntry?.id));
    journalTracker('Journal entry', 'Report post modal opened');
  };

  // if all comments are deleted, ensure return to collapsed state
  useEffect(() => {
    if (commentsCount === 0) {
      setCommentsCollapsed(true);
    }
  }, [commentsCount]);

  const renderOptionsDropdown = () => (
    <OptionsDropdownWrapper>
      <Dropdown alignRight>
        <Dropdown.Toggle className='dropdown-toggle' aria-label='Entry options'>
          <OptionsIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={24}
              icon='dots'
              color={ColorGray}
            />
          </OptionsIconContainer>
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {currentUserIsAuthor ? (
            <>
              <Dropdown.Item onClick={handleEditClick}>
                <IcomoonReact iconSet={iconSet} size={15} icon='pencil' />
                <Text styledTextProps={DROPDOWN_TEXT_PROPS}>Edit</Text>
              </Dropdown.Item>

              <Dropdown.Item onClick={handleDeleteClick}>
                <IcomoonReact iconSet={iconSet} size={15} icon='trashcan' />
                <Text styledTextProps={DROPDOWN_TEXT_PROPS}>Delete</Text>
              </Dropdown.Item>
            </>
          ) : (
            <Dropdown.Item onClick={handleReportJournalEntryClick}>
              <IcomoonReact iconSet={iconSet} size={15} icon='warning' />
              <Text styledTextProps={DROPDOWN_TEXT_PROPS}>Report Post</Text>
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </OptionsDropdownWrapper>
  );

  const renderHeader = () => (
    <>
      {showGradientBar && (
        <GradientBar gradientBarColors={createdByUserGradientColors} />
      )}
      <EntryHeaderContainer>
        <DateAndPrivacyContainer>
          <EntryDate>{createdAt}</EntryDate>
          {isPrivate && (
            <PrivacyIconContainer>
              <IcomoonReact
                iconSet={iconSet}
                size={16}
                icon='invisible'
                color={ColorGray}
              />
            </PrivacyIconContainer>
          )}
        </DateAndPrivacyContainer>
        {renderOptionsDropdown()}
      </EntryHeaderContainer>
    </>
  );

  const renderTextContent = () => (
    <>
      <EntryTitle title={title}>{title || <em>Untitled</em>}</EntryTitle>
      <EntryText>{body}</EntryText>
    </>
  );

  const renderMedia = () => {
    const hasValidMedia = !!media?.length && media?.some((mediaObj) => mediaObj?.id);
    if (!hasValidMedia) return null;

    return (
      <MediaContainer>
        <MediaHeader>MEDIA</MediaHeader>
        <MediaItems>
          {media.map((mediaObj, index) => {
            if (!mediaObj?.id) return null;
            const isVideo = mediaObj.type === 'VIDEO';
            const MediaWrapper = isVideo ? MediaBoxDiv : MediaBox;

            return (
              <MediaItem key={mediaObj.id}>
                <MediaWrapper
                  onClick={
                    !isVideo
                      ? () => handleImageClick(mediaObj.mediaUrl)
                      : undefined
                  }
                  aria-label={
                    !isVideo
                      ? `View image - entry media ${index + 1}`
                      : undefined
                  }
                >
                  {mediaObj.mediaUrl && (
                    <MediaImage
                      src={getImageSource(mediaObj)}
                      alt={`Entry media ${index + 1}`}
                    />
                  )}
                  {isVideo && (
                    <PlayIconContainer
                      onClick={(e) => handleVideoClick(e, mediaObj.mediaUrl)}
                      aria-label={`Play video - entry media ${index + 1}`}
                    >
                      <IcomoonReact
                        iconSet={iconSet}
                        size={40}
                        icon='play-filled'
                        color={ColorWhite}
                      />
                    </PlayIconContainer>
                  )}
                </MediaWrapper>
                {mediaObj.title && <MediaTitle>{mediaObj.title}</MediaTitle>}
              </MediaItem>
            );
          })}
        </MediaItems>
      </MediaContainer>
    );
  };

  const renderEntryDetails = () => (
    <EntryDetailsContainer>
      <CommentsContainer
        aria-label={commentsCollapsed ? 'View comments' : 'Hide comments'}
        count={commentsCount}
        onClick={handleCommentsToggle}
      >
        <CommentCount commentsCollapsed={commentsCollapsed}>
          <CommentIconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={20}
              icon='comment'
              color={commentsCollapsed ? ColorGray : 'orange'}
            />
          </CommentIconContainer>
          <CommentCountText commentsCollapsed={commentsCollapsed}>
            {commentsCount === 1
              ? `${commentsCount} Comment`
              : `${commentsCount} Comments`}
          </CommentCountText>
        </CommentCount>
      </CommentsContainer>
      <AuthorInfoContainer>
        <AuthorName>{createdByUserName}</AuthorName>
        <AuthorAvatarContainer>
          <Avatar
            linearGradient={createdByUserGradientColors}
            showGradientRing
            avatar={createdByUserPic || ''}
            name={createdByUserName}
            size={32}
          />
        </AuthorAvatarContainer>
      </AuthorInfoContainer>
    </EntryDetailsContainer>
  );

  const renderCommentsList = () => {
    if (commentsCollapsed || commentsCount === 0) return null;
    return <CommentList journalEntry={journalEntry} />;
  };

  const renderAddCommentBox = () => {
    if (hasCommentEditing) {
      return (
        <AddCommentBox
          commentText={editedComment?.comment?.commentText || ''}
          entryId={editedComment?.entryId}
          commentId={editedComment?.comment?.id}
          isEditing
          setCommentsCollapsed={() => {}}
        />
      );
    }

    return (
      <AddCommentBox
        commentText={newCommentText}
        setCommentText={setNewCommentText}
        entryId={journalEntry?.id}
        commentId={null}
        isEditing={false}
        setCommentsCollapsed={setCommentsCollapsed}
      />
    );
  };

  return (
    <JournalEntryContainer>
      {renderHeader()}
      {renderTextContent()}
      {renderMedia()}
      {renderEntryDetails()}
      {renderCommentsList()}
      {renderAddCommentBox()}
    </JournalEntryContainer>
  );
};

JournalEntry.propTypes = {
  journalEntry: PropTypes.shape({
    associatedExerciseName: PropTypes.string,
    body: PropTypes.string,
    comments: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
    createdAt: PropTypes.string,
    createdByUserId: PropTypes.number,
    createdByUserGradientColors: PropTypes.arrayOf(PropTypes.string),
    createdByUserName: PropTypes.string,
    createdByUserPic: PropTypes.string,
    id: PropTypes.number,
    media: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
    title: PropTypes.string,
    private: PropTypes.bool,
  }).isRequired,
};

export default JournalEntry;
